import { useEffect, useState } from "react";
import { useUser } from "@/contexts/UserContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EmotionChart from "@/components/EmotionChart";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Target,
  TrendingUp,
  Users,
  User,
  Loader2,
  BookOpen,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchGrowthGoals, createGrowthGoal } from "@/components/chat/services/goalService";
import AddGrowthGoalModal from "@/components/modals/AddGrowthGoalModal";
import { format } from "date-fns";
import { toast } from "@/components/ui/use-toast";
import { wellnessService } from "@/services/wellnessService";
import { useLocation } from "react-router-dom";
import { enUS } from "date-fns/locale";
import GrowthAISuggestionsWidget from "@/components/growth/GrowthAISuggestionsWidget";
import AIGrowthInsights from "@/components/growth/AIGrowthInsights";
import SkillNetworkEnhanced from "@/components/skills/SkillNetworkEnhanced";
import EnhancedGrowthGoals from "@/components/growth/EnhancedGrowthGoals";

interface ReflectionHistoryItem {
  id: string;
  date: string;
  mood: number;
  reflection: string;
  wins?: string;
  blockers?: string;
  energy?: number;
  focus?: number;
  helpNeeded?: boolean;
  helpWith?: string;
}



const Profile = () => {
  const { user, loading, isAuthenticated } = useUser();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "overview"
  );
  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);
  const [growthGoals, setGrowthGoals] = useState([]);
  const [isLoadingGoals, setIsLoadingGoals] = useState(true);
  const [error, setError] = useState(null);
  
  // New state for reflection history
  const [reflectionHistory, setReflectionHistory] = useState<ReflectionHistoryItem[]>([]);
  const [isLoadingReflections, setIsLoadingReflections] = useState(true);
  const [reflectionsError, setReflectionsError] = useState<string | null>(null);

  // Update active tab when location state changes
  useEffect(() => {
    if (location.state?.activeTab) {
      console.log("Setting active tab to:", location.state.activeTab);
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    console.log("Profile page loaded. User:", user);
    console.log("Loading state:", loading);
    console.log("Is authenticated:", isAuthenticated);
  }, [user, loading, isAuthenticated]);

  const fetchGoals = async () => {
    if (!user?.id) return;
    setIsLoadingGoals(true);
    try {
      const goals = await fetchGrowthGoals(user.id);
      setGrowthGoals(goals || []);
      console.log("Goals fetched:", goals);
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la récupération des growthGoals:", err);
      setError("Impossible de charger les objectifs de croissance.");
    } finally {
      setIsLoadingGoals(false);
    }
  };


  // Récupérer growthGoals de manière asynchrone
  useEffect(() => {
    if (user?.id) {
      fetchGoals();
    }
  }, [user?.id]);

  const handleAddGoal = async (goalData: {
    title: string;
    category: string;
    dueDate: Date;
    description: string;
    progress: number;
  }) => {
    if (!user) return;

    try {
      const newGoal = await createGrowthGoal(
        user.id,
        {
          title: goalData.title,
          category: goalData.category,
          dueDate: format(goalData.dueDate, "yyyy-MM-dd"),
          description: goalData.description,
          progress: goalData.progress,
          userId: user.id,
        }
      );

      if (newGoal) {
        setGrowthGoals((prev) => [...prev, newGoal]);
        toast({
          title: "Goal added",
          description: "Your new growth goal has been added successfully.",
        });
      }
    } catch (error) {
      console.error("Error adding goal:", error);
      toast({
        title: "Error",
        description: "Failed to add the goal. Please try again.",
        variant: "destructive",
      });
    }
  };


  // Function to fetch reflection history from database
  const fetchReflectionHistory = async () => {
    if (!user?.id) {
      setIsLoadingReflections(false);
      return;
    }

    setIsLoadingReflections(true);
    setReflectionsError(null);

    try {
      // Fetch check-ins with reflections from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const checkIns = await wellnessService.getCheckIns({
        startDate: thirtyDaysAgo.toISOString(),
        hasReflection: true,
      });

      if (!checkIns || !Array.isArray(checkIns)) {
        setReflectionHistory([]);
        return;
      }

      // Filter check-ins with reflections and transform the data
      const transformedHistory: ReflectionHistoryItem[] = checkIns
        .filter((checkIn: any) => checkIn.reflection && checkIn.reflection.trim() !== '')
        .map((checkIn: any) => ({
          id: checkIn.id,
          date: checkIn.date,
          mood: checkIn.mood,
          reflection: checkIn.reflection,
          wins: checkIn.wins,
          blockers: checkIn.blockers,
          energy: checkIn.energy,
          focus: checkIn.focus,
          helpNeeded: checkIn.helpNeeded || checkIn.help_needed,
          helpWith: checkIn.helpWith || checkIn.help_with,
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setReflectionHistory(transformedHistory);
    } catch (error) {
      console.error("Error fetching reflection history:", error);
      setReflectionsError("Failed to load reflection history");
    } finally {
      setIsLoadingReflections(false);
    }
  };

  // Function to get mood label from mood number
  const getMoodLabel = (mood: number): string => {
    switch (mood) {
      case 1:
        return "low";
      case 2:
        return "okay";
      case 3:
        return "okay";
      case 4:
        return "good";
      case 5:
        return "great";
      default:
        return "okay";
    }
  };

  // Function to get mood variant for badge
  const getMoodVariant = (mood: number): "default" | "secondary" | "outline" | "destructive" => {
    switch (mood) {
      case 1:
      case 2:
        return "destructive";
      case 3:
        return "outline";
      case 4:
        return "secondary";
      case 5:
        return "default";
      default:
        return "outline";
    }
  };

  // Function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy", { locale: enUS });
    } catch {
      return dateString;
    }
  };

  // Fetch reflection history when component mounts
  useEffect(() => {
    if (user?.id) {
      fetchReflectionHistory();
    }
  }, [user?.id]);

  // Refresh reflection history when reflections tab becomes active
  useEffect(() => {
    if (activeTab === "reflections") {
      fetchReflectionHistory();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Enhanced header with progress overview */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-card dark:to-muted/50 rounded-lg p-6 border border-emerald-200 dark:border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-emerald-100 dark:bg-muted rounded-lg">
                  <User className="h-6 w-6 text-emerald-600 dark:text-primary" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-emerald-900 dark:text-foreground">
                  My Growth Journey
                </h1>
              </div>
              <p className="text-muted-foreground text-lg">
                Track your progress and reflect on your development
              </p>
            </div>

          </div>

          {/* Quick progress indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/60 dark:bg-card/60 p-3 rounded-lg border border-emerald-100 dark:border-border">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="h-4 w-4 text-emerald-600 dark:text-primary" />
                <span className="text-sm font-medium text-emerald-700 dark:text-foreground">Reflections</span>
              </div>
              <div className="text-xl font-bold text-emerald-800 dark:text-foreground">{reflectionHistory.length}</div>
              <p className="text-xs text-emerald-600 dark:text-muted-foreground">This month</p>
            </div>
            
            <div className="bg-white/60 dark:bg-card/60 p-3 rounded-lg border border-blue-100 dark:border-border">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-4 w-4 text-blue-600 dark:text-primary" />
                <span className="text-sm font-medium text-blue-700 dark:text-foreground">Goals</span>
              </div>
              <div className="text-xl font-bold text-blue-800 dark:text-foreground">{growthGoals.length}</div>
              <p className="text-xs text-blue-600 dark:text-muted-foreground">Active goals</p>
            </div>
            
            <div className="bg-white/60 dark:bg-card/60 p-3 rounded-lg border border-purple-100 dark:border-border">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-purple-600 dark:text-primary" />
                <span className="text-sm font-medium text-purple-700 dark:text-foreground">Completed</span>
              </div>
              <div className="text-xl font-bold text-purple-800 dark:text-foreground">
                {growthGoals.filter(g => g.progress >= 100).length}
              </div>
              <p className="text-xs text-purple-600 dark:text-muted-foreground">Goals finished</p>
            </div>
            
            <div className="bg-white/60 dark:bg-card/60 p-3 rounded-lg border border-orange-100 dark:border-border">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-orange-600 dark:text-primary" />
                <span className="text-sm font-medium text-orange-700 dark:text-foreground">Progress</span>
              </div>
              <div className="text-xl font-bold text-orange-800 dark:text-foreground">
                {growthGoals.length > 0 ? Math.round(growthGoals.reduce((sum, g) => sum + g.progress, 0) / growthGoals.length) : 0}%
              </div>
              <p className="text-xs text-orange-600 dark:text-muted-foreground">Avg completion</p>
            </div>
          </div>
        </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="reflections">Reflections</TabsTrigger>
              <TabsTrigger value="goals">Growth Goals</TabsTrigger>
              <TabsTrigger value="network">Skill Network</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Wellbeing Trends</CardTitle>
                    <CardDescription>
                      Your energy, focus and motivation over time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <EmotionChart />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Key Insights</CardTitle>
                    <CardDescription>
                      Based on your check-in patterns
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-2 rounded-full h-fit">
                          <Clock size={18} />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm text-foreground">
                            Peak Productivity
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Your focus peaks mid-week, especially on Wednesday
                            mornings.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 p-2 rounded-full h-fit">
                          <TrendingUp size={18} />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm text-foreground">
                            Energy Pattern
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Your energy tends to dip on Fridays. Consider
                            scheduling lighter work.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <div className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-2 rounded-full h-fit">
                          <Users size={18} />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm text-foreground">
                            Team Alignment
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            You're most aligned with your team's energy on
                            Tuesdays and Thursdays.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <GrowthAISuggestionsWidget />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Growth Goals Progress</CardTitle>
                  <CardDescription>
                    Track your progress toward key development areas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingGoals ? (
                    <div className="text-center">
                      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                      <p className="text-muted-foreground mt-2">
                        Loading growth goals...
                      </p>
                    </div>
                  ) : error ? (
                    <p className="text-destructive">{error}</p>
                  ) : growthGoals.filter(goal => (goal.status || 'active') === 'active').length === 0 ? (
                    <p className="text-muted-foreground">
                      No active growth goals found.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {growthGoals.filter(goal => (goal.status || 'active') === 'active').map((goal, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium">{goal.title}</h3>
                            <Badge
                              variant={
                                goal.progress >= 100
                                  ? "default"
                                  : goal.progress >= 75
                                  ? "secondary"
                                  : goal.progress >= 50
                                  ? "outline"
                                  : "destructive"
                              }
                            >
                              {goal.progress}% complete
                            </Badge>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2 mb-2">
                            <div
                              className="bg-primary rounded-full h-2"
                              style={{ width: `${goal.progress}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {goal.progress}% complete
                            </span>
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Calendar size={14} />
                              Due {goal.due}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reflections" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Reflection History
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchReflectionHistory}
                      disabled={isLoadingReflections}
                    >
                      {isLoadingReflections ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Refresh"
                      )}
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Your past check-ins and reflections
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingReflections ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-muted-foreground">Loading reflection history...</p>
                      </div>
                    </div>
                  ) : reflectionsError ? (
                    <div className="text-center py-8">
                      <p className="text-destructive mb-2">{reflectionsError}</p>
                      <Button 
                        variant="outline" 
                        onClick={fetchReflectionHistory}
                        className="mt-2"
                      >
                        Try Again
                      </Button>
                    </div>
                  ) : reflectionHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No reflections yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Start your daily check-ins to build your reflection history
                      </p>
                      <Button onClick={() => setActiveTab("overview")}>
                        Start Check-in
                      </Button>
                    </div>
                  ) : (
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-6">
                        {reflectionHistory.map((item) => (
                          <div
                            key={item.id}
                            className="border rounded-lg p-4 space-y-3"
                          >
                            <div className="flex justify-between items-center">
                              <h3 className="font-medium">{formatDate(item.date)}</h3>
                              <Badge variant={getMoodVariant(item.mood)}>
                                {getMoodLabel(item.mood)}
                              </Badge>
                            </div>
                            
                            {/* Reflection */}
                            <div>
                              <p className="text-muted-foreground">
                                {item.reflection}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="goals" className="space-y-6">
              {/* AI Growth Insights Section */}
              <AIGrowthInsights />
              
              {/* Enhanced Growth Goals with AI Integration */}
              <EnhancedGrowthGoals />
            </TabsContent>

            <TabsContent value="network" className="space-y-6">
              <SkillNetworkEnhanced />
            </TabsContent>

          </Tabs>

        <AddGrowthGoalModal
          isOpen={isAddGoalModalOpen}
          onOpenChange={setIsAddGoalModalOpen}
          onSubmit={handleAddGoal}
        />
      </div>
  );
};

export default Profile;
